import React, { useEffect, useRef } from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';
import { pluralize } from '~libs/stringarly';

import { ErrorResponse, Spinner } from '~components';

import { DOCUMENT_TYPES } from '~constants/common';

import { BudgetPreviewRow, useBulkCopyPreviewMutation } from '~support-at-home/actions/budgets';

import CategoryMoveNotice, { groupMovesFromMovedServices } from '../CategoryMoveNotice';

import BudgetSampleList from './BudgetSampleList';
import { BulkCopyCsvJob } from './bulkCopyCsvJobs';
import FailureList from './FailureList';
import GenerateImpactCsvButton from './GenerateImpactCsvButton';

interface ComponentProps {
  value: {
    budget_ids: string[];
    start_date: string;
    end_date: string;
    price_plan_revision_id?: string;
    apply_category_moves?: boolean;
  };
  onChange?: (name: string, value: any) => any;
}

/** The bulk run's hint. It has per-budget counts on screen, not sections. */
const BULK_APPLIED_HINT =
  'check the counts below before confirming, or leave them out if you would rather add them yourself.';

const PreviewPanel: React.FC<ComponentProps> = ({ value, onChange }) => {
  const { budget_ids, start_date, end_date, price_plan_revision_id, apply_category_moves } = value;
  // Undefined until the provider touches the switch. Moving the services is what the run does unless
  // they say otherwise, so only an explicit false turns it off.
  const applyCategoryMoves = apply_category_moves !== false;
  const [preview, { data, error, isLoading }] = useBulkCopyPreviewMutation();

  useEffect(() => {
    preview({ budget_ids, start_date, end_date, target_revision_id: price_plan_revision_id ?? null });
  }, []);

  // Gate "Confirm Copy": only enable it once the dry-run has settled successfully so the
  // coordinator can't skip past the impact preview into the real copy. The wizard's disableNext
  // reads this from Formik values. Guarded by a ref because onChange's identity churns every
  // Formik render (see ListAndConfirm), which would otherwise loop.
  const readyRef = useRef<boolean | null>(null);
  useEffect(() => {
    const ready = !isLoading && !error && !!data;
    if (readyRef.current === ready) return;
    readyRef.current = ready;
    onChange?.('preview_ready', ready);
  }, [isLoading, error, data, onChange]);

  // Also gate it on there being something left to copy: with only a sample assessed, the only honest reason
  // to block Confirm is that every budget actually looked at was un-copyable. Published separately from
  // preview_ready so the two reasons for a disabled Confirm stay distinguishable (see bulkCopy.ts step 3).
  const hasCopyableRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (!data) return;
    // Against an older API (deploy skew), sample_size/sample_failed can arrive undefined. A missing count
    // means "unknown", not "nothing to copy", so leave the flag UNPUBLISHED rather than defaulting either
    // number: bulkCopy.ts's disableNext tests `=== false` specifically, so an unpublished flag leaves
    // Confirm enabled. Failing open is the recoverable choice here, because the copy itself rejects
    // un-copyable budgets and reports them at step 4, whereas failing closed with no on-screen explanation
    // is a dead end.
    if (!Number.isFinite(data.sample_size) || !Number.isFinite(data.sample_failed)) return;
    const hasCopyable = data.sample_size > 0 && data.sample_failed < data.sample_size;
    if (hasCopyableRef.current === hasCopyable) return;
    hasCopyableRef.current = hasCopyable;
    onChange?.('has_copyable_budgets', hasCopyable);
  }, [data, onChange]);

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner loading={isLoading} />
        <p className="mt-3 text-muted">Checking affected budgets...</p>
      </div>
    );
  }

  if (error) return <ErrorResponse message={getErrorMessage(error)} />;

  if (!data) return null;

  // Budgets carrying at least one category move. Counted over budgets, not services, because the
  // sentence it feeds is about budgets - and a budget with three moved lines is still one budget.
  // Both this and moveGroups read the assessed sample, which is all the preview returns.
  const movedBudgetCount = data.budgets.filter((budget) => (budget.moved_count ?? 0) > 0).length;
  // Every moved line the preview found, collapsed into one statement of the change. Built from what
  // the preview DETECTED rather than from the choice, so it survives the switch being turned off.
  const moveGroups = groupMovesFromMovedServices(data.budgets.flatMap((budget) => budget.moved_services ?? []));

  /**
   * Services that will not come across on this budget.
   *
   * The preview is always reconciled as though the moves are being carried across - that is what
   * keeps the change detectable after a provider switches it off, and a detection that disappeared
   * with the choice would take the switch and the column with it. So the server's removed_count is
   * the figure for carrying them; declining puts the moved services back among the removals, and the
   * count has to say so rather than quietly under-reporting what the run is about to leave behind.
   */
  const removedCount = (row: BudgetPreviewRow): number =>
    applyCategoryMoves ? row.removed_count : row.removed_count + (row.moved_count ?? 0);

  /**
   * Services actually being moved on this budget, which is none once the move is declined.
   *
   * The two columns have to add up to the number of services affected. Leaving this at the detected
   * figure while the removals absorbed the same services counted them twice - a budget with one
   * personal care line and one genuine removal read as "2 removed, 1 moved", which is three services
   * on a budget that has two. The COLUMN still appears whenever a move was detected; it is the
   * figure in it that follows the choice.
   */
  const movedCount = (row: BudgetPreviewRow): number => (applyCategoryMoves ? (row.moved_count ?? 0) : 0);

  // Budgets the copy would reject outright (e.g. no resolvable funding source). Listed here so they're known
  // BEFORE the copy runs; budgets beyond the sample can still surface at step 4 (TAV-2649a). These props keep
  // one code path across both wizard steps: at Preview they're degenerate (no break ever renders) because at
  // most 15 budgets are assessed here.
  const failures = data.failures ?? [];
  const failureList = (
    <FailureList
      failures={failures}
      headingSuffix="Will Not Be Copied"
      idPrefix="bulk-copy-preview"
      firstCount={data.failures_first_count ?? failures.length}
      lastCount={data.failures_last_count ?? 0}
      total={data.failures_total ?? failures.length}
    />
  );

  // The headline below only claims the full selection size: the full picture (every selected budget, not
  // just the sample) is what the downloadable Full Impact CSV is for. The needs-review line beneath it, and
  // the sample-scoped clean/failed breakdown shown via the tables below, are each scoped honestly to the
  // sample actually assessed rather than folded into a headline they can't truthfully speak for.
  const generateCsvJob: BulkCopyCsvJob = {
    document_type: DOCUMENT_TYPES.BULK_COPY.PREVIEW_CSV,
    budget_ids,
    start_date,
    end_date,
    target_revision_id: price_plan_revision_id ?? null,
  };

  // `rows`/`firstCount`/`lastCount` describe only the COPYABLE subset of the sample (data.budgets excludes
  // data.failures), while `data.total` counts the whole selection. Passing `sample_size` (how many ids the
  // sample actually assessed, copyable or not) as `assessed` is what keeps the break row and its caption
  // from firing just because some sampled ids failed (TAV-3258 review): see sampleTableRows.ts.
  const sampleFirstCount = data.first_count ?? data.budgets.length;
  const sampleLastCount = data.last_count ?? 0;

  // Against an older API (deploy skew), sample_needs_review/sample_size can arrive undefined; a missing
  // count is "unknown", not "zero", so the line is omitted entirely rather than rendering "undefined of
  // undefined" (mirrors the hasCopyable effect's reasoning above).
  const hasUsableReviewCount =
    Number.isFinite(data.sample_needs_review) && Number.isFinite(data.sample_size) && data.sample_size > 0;
  const assessedNoun = hasUsableReviewCount ? pluralize(data.sample_size, 'budget').pluralNoun : '';

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <p className="mb-0">
          <strong>{data.total}</strong> {data.total === 1 ? 'budget' : 'budgets'} selected for copy.
        </p>
        <GenerateImpactCsvButton job={generateCsvJob} dataCy="bulk-copy-preview-generate-csv-button" />
      </div>
      {hasUsableReviewCount && (
        <p className="text-muted small mb-3">
          {data.sample_needs_review} of the {data.sample_size} {assessedNoun} assessed need review after copy.
        </p>
      )}
      <CategoryMoveNotice
        groups={moveGroups}
        applyCategoryMoves={applyCategoryMoves}
        onToggle={(next) => onChange?.('apply_category_moves', next)}
        scopeLabel={movedBudgetCount === 1 ? 'the budget being copied' : 'the budgets being copied'}
        appliedHint={BULK_APPLIED_HINT}
        dataCy="bulk-copy-category-moves"
      />
      <BudgetSampleList
        rows={data.budgets.map((budget) => ({
          ...budget,
          removed_count: removedCount(budget),
          moved_count: movedCount(budget),
        }))}
        showMoved={moveGroups.length > 0}
        firstCount={sampleFirstCount}
        lastCount={sampleLastCount}
        total={data.total}
        assessed={data.sample_size}
        caption={`Showing the first ${sampleFirstCount} and last ${sampleLastCount} of ${data.total} selected budgets.`}
        idPrefix="bulk-copy-preview"
      />
      {failureList}
    </div>
  );
};

export default PreviewPanel;
